import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { parseYouTubeVideoId } from "@/utils/youtube";

export interface ExternalStreamFormProps {
  /** Receives the parsed 11-char video ID and optional trimmed label. */
  onAdd: (videoId: string, label?: string) => void;
  onCancel: () => void;
}

/**
 * Inline popover for pinning a YouTube stream on the Team Live multiview.
 * Owns input state and validation display; the page owns persistence.
 * Rendered inside a `relative` header wrapper; positions itself absolute.
 */
export function ExternalStreamForm({ onAdd, onCancel }: ExternalStreamFormProps) {
  const { t } = useTranslation("pages");
  const [input, setInput] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const videoId = parseYouTubeVideoId(input);
    if (!videoId) {
      setError(t("teamLive.externalStreamInvalid"));
      return;
    }
    onAdd(videoId, label.trim() || undefined);
  };

  return (
    <form
      onSubmit={submit}
      data-testid="external-stream-form"
      className="absolute right-0 top-full z-10 mt-2 flex w-80 flex-col gap-2 rounded-lg border border-white/10 bg-[#0d0d16] p-3 shadow-xl"
    >
      <input
        autoFocus
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setError(null);
        }}
        placeholder={t("teamLive.externalStreamUrlPlaceholder")}
        aria-label={t("teamLive.externalStreamUrlPlaceholder")}
        aria-invalid={!!error}
        aria-describedby={error ? "external-stream-error" : undefined}
        className="min-h-[38px] rounded-lg border border-white/15 bg-black/40 px-3 text-sm text-white placeholder:text-white/40 focus:border-sky-500 focus:outline-none"
      />
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={t("teamLive.externalStreamLabelPlaceholder")}
        aria-label={t("teamLive.externalStreamLabelPlaceholder")}
        className="min-h-[38px] rounded-lg border border-white/15 bg-black/40 px-3 text-sm text-white placeholder:text-white/40 focus:border-sky-500 focus:outline-none"
      />
      {error ? (
        <p id="external-stream-error" role="alert" className="text-xs text-red-400">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[38px] rounded-lg border border-white/20 px-3 text-sm font-semibold text-white/80 hover:bg-white/10"
        >
          {t("teamLive.externalStreamCancel")}
        </button>
        <button
          type="submit"
          className="min-h-[38px] rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white hover:bg-sky-700"
        >
          {t("teamLive.externalStreamSubmit")}
        </button>
      </div>
    </form>
  );
}
